'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { createApiKey, listApiKeys, revokeApiKey } from '@/service/api-key.api'
import {
  ApiKeyInfo,
  CreateApiKeyRequest,
  ListApiKeysResponse,
  RevokeApiKeyResponse,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const apiKeyKeys = {
  all: (userId?: string) => ['api-keys', userId] as const,
  list: (userId?: string) => [...apiKeyKeys.all(userId), 'list'] as const,
}

export const useListApiKeys = () => {
  const { user, accessToken } = useAuth()
  return useQuery({
    queryKey: apiKeyKeys.list(user?.userId),
    queryFn: listApiKeys,
    // An unverified email gets the standard auth error — don't ask.
    enabled: Boolean(user?.emailVerified) && Boolean(accessToken),
    // A failed list must show as an error, never as "no keys"; one retry is plenty.
    retry: 1,
  })
}

/**
 * Mints a key. Deliberately not a useMutation: the response carries the full key string, and
 * React Query would keep it in the mutation cache after the dialog closes. The secret goes
 * straight back to the caller only; the list cache gets just the non-secret ApiKeyInfo,
 * prepended locally instead of refetching.
 *
 * Never retried: a failed attempt counts toward the account's step-up throttle.
 */
export const useCreateApiKey = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [isPending, setIsPending] = useState(false)

  const create = useCallback(
    async (payload: CreateApiKeyRequest): Promise<string> => {
      setIsPending(true)
      try {
        const response = await createApiKey(payload)
        const key = response.key
        if (key) {
          queryClient.setQueryData<ListApiKeysResponse>(apiKeyKeys.list(user?.userId), (prev) =>
            prev
              ? { ...prev, keys: [key, ...prev.keys.filter((k) => k.keyId !== key.keyId)] }
              : prev
          )
        }
        return response.apiKey as string
      } finally {
        setIsPending(false)
      }
    },
    [queryClient, user?.userId]
  )

  return { create, isPending }
}

export const useRevokeApiKey = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: RevokeApiKeyResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: (response, { keyId }) => {
      // Mark the row revoked right away, then let the server's copy (with its own revoked_at)
      // replace it.
      const revokedAt = String(Math.floor(Date.now() / 1000))
      queryClient.setQueryData<ListApiKeysResponse>(apiKeyKeys.list(user?.userId), (prev) =>
        prev
          ? {
              ...prev,
              keys: prev.keys.map((k): ApiKeyInfo => (k.keyId === keyId ? { ...k, revokedAt } : k)),
            }
          : prev
      )
      queryClient.invalidateQueries({ queryKey: apiKeyKeys.list(user?.userId) })
      onSuccess?.(response)
    },
    onError,
  })

  return { mutateAsync, isPending, revokingKeyId: isPending ? variables?.keyId : undefined }
}
